/**
 * App-owned document viewer surface.
 *
 * Renders PDFs (via pdf.js → canvas) or single-image documents into a
 * scrollable, virtualised page list with absolute-positioned overlay
 * slots per page. Overlay/editor code targets the normalized page model
 * and the canonical CSS-pixel document coordinate space; the viewer
 * handles scale, rotation, and fit logic.
 *
 * This component intentionally does NOT render an iframe or <embed>:
 * every page is a real <canvas> in a real DOM container we own, so
 * children can be positioned absolutely on top.
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Maximize2,
  Minus,
  Plus,
  RectangleHorizontal,
  RotateCw,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { renderedPageSize } from './coordinates'
import { PageCanvas, PagePlaceholder } from './PageCanvas'
import { useImageDocument } from './useImageDocument'
import { usePdfDocument } from './usePdfDocument'
import { useViewerState } from './useViewerState'
import type {
  DocumentSource,
  FitMode,
  PageModel,
  PageRenderer,
  Rotation,
} from './types'

const PDF_MIME = 'application/pdf'
const IMAGE_MIME_PREFIX = 'image/'
/** How many off-screen pages to keep mounted on each side of the viewport. */
const PAGE_BUFFER = 1
/**
 * After the user stops scrolling, wait this long before clearing the
 * `data-scrolling="true"` flag on the scroll container. Overlay layers
 * that opt in (via the `scrolling` arg passed to `renderOverlay`) can use
 * the flag to suspend expensive search/highlight work while a fast scroll
 * is in flight.
 */
const SCROLL_IDLE_MS = 100

/**
 * Imperative jump request — bump `token` to scroll to `pageNumber` (1-based).
 * Repeating the same `pageNumber` only re-fires when `token` changes, so
 * clicking the same review pin twice reliably re-scrolls.
 */
export interface PageJumpRequest {
  pageNumber: number
  token: number
}

export interface DocumentViewerProps {
  source: DocumentSource | null
  /** Initial fit mode for a freshly mounted source. */
  initialFitMode?: FitMode
  /**
   * Render overlay layers per page. Called with the page model and the
   * rendered (CSS-pixel, post-scale, post-rotation) container size so
   * overlay code can position children using `documentToView` from
   * `./coordinates`.
   */
  renderOverlay?: (args: {
    page: PageModel
    scale: number
    rotation: Rotation
    rendered: { width: number; height: number }
    /**
     * `true` while the scroll container is being actively scrolled (and
     * for a short tail after). Overlay layers can short-circuit expensive
     * per-page work (large search highlight passes, dense comment threads,
     * …) while scrolling and re-render once it settles.
     */
    scrolling: boolean
  }) => ReactNode
  /** Empty-state when no source has been chosen. */
  emptyState?: ReactNode
  className?: string
  /** Auto-focus the scroll container so keyboard nav works on mount. */
  autoFocus?: boolean
  /**
   * Imperative page-jump request from outside the viewer (e.g. a side
   * panel pin click). The viewer scrolls to `pageNumber` and bumps its
   * current-page state when `token` changes.
   */
  pageJump?: PageJumpRequest | null
  /**
   * Fires with the 1-based page number whenever the viewer's active page
   * changes (initial mount, scroll, keyboard nav, toolbar, page-jump).
   * Callers use this to keep ambient UI like the review composer's
   * "current page" anchor in sync with what the user is actually viewing.
   */
  onCurrentPageChange?: (pageNumber: number) => void
}

export function DocumentViewer({
  source,
  initialFitMode = 'width',
  renderOverlay,
  emptyState,
  className,
  autoFocus = false,
  pageJump = null,
  onCurrentPageChange,
}: DocumentViewerProps) {
  if (!source) {
    return (
      <div
        className={cn(
          'flex h-64 items-center justify-center text-sm text-muted-foreground',
          className,
        )}
        data-testid="viewer-empty"
      >
        {emptyState ?? 'No document loaded.'}
      </div>
    )
  }

  const isPdf = source.mimeType === PDF_MIME
  const isImage = source.mimeType.startsWith(IMAGE_MIME_PREFIX)

  if (!isPdf && !isImage) {
    return (
      <div
        className={cn(
          'flex h-64 items-center justify-center text-sm text-muted-foreground',
          className,
        )}
        data-testid="viewer-unsupported"
      >
        Unsupported document type: {source.mimeType}
      </div>
    )
  }

  // Two source-typed inner components so the hook order matches the
  // chosen renderer for the entire lifetime of a given source. Switching
  // mime type unmounts and remounts the inner viewer naturally.
  return isPdf ? (
    <PdfViewer
      key={source.id}
      source={source}
      initialFitMode={initialFitMode}
      renderOverlay={renderOverlay}
      className={className}
      autoFocus={autoFocus}
      pageJump={pageJump}
      onCurrentPageChange={onCurrentPageChange}
    />
  ) : (
    <ImageViewer
      key={source.id}
      source={source}
      initialFitMode={initialFitMode}
      renderOverlay={renderOverlay}
      className={className}
      autoFocus={autoFocus}
      pageJump={pageJump}
      onCurrentPageChange={onCurrentPageChange}
    />
  )
}

function PdfViewer(props: InnerViewerProps) {
  // Prefer the in-memory Blob (live-preview render) when present so pdf.js
  // gets the bytes directly via `getDocument({ data })`. Falls back to the
  // URL string until the first live render lands.
  const renderer = usePdfDocument(props.source.data ?? props.source.url)
  return <InnerViewer {...props} renderer={renderer} />
}

function ImageViewer(props: InnerViewerProps) {
  const renderer = useImageDocument(props.source.url)
  return <InnerViewer {...props} renderer={renderer} />
}

type InnerViewerProps = Omit<DocumentViewerProps, 'source'> & {
  source: DocumentSource
}

function InnerViewer({
  renderer,
  initialFitMode = 'width',
  renderOverlay,
  className,
  autoFocus,
  pageJump,
  onCurrentPageChange,
}: InnerViewerProps & { renderer: PageRenderer }) {
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const pageRefs = useRef<Map<number, HTMLDivElement>>(new Map())
  const [viewport, setViewport] = useState<{ width: number; height: number }>(
    { width: 0, height: 0 },
  )
  const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([0]))
  const [isScrolling, setIsScrolling] = useState(false)

  const viewer = useViewerState({
    pages: renderer.pages,
    initialFitMode,
  })
  const { state, applyFit } = viewer
  const setCurrentPageIndex = viewer.setCurrentPageIndex

  // Resize observation drives fit-mode rescaling and the IntersectionObserver
  // root margin computation below.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const update = () => {
      setViewport({ width: el.clientWidth, height: el.clientHeight })
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (viewport.width === 0 || viewport.height === 0) return
    if (renderer.pages.length === 0) return
    applyFit(viewport)
  }, [viewport, renderer.pages, state.fitMode, state.rotation, applyFit])

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (renderer.pages.length === 0) return
    const nextViewport = { width: el.clientWidth, height: el.clientHeight }
    if (nextViewport.width === 0 || nextViewport.height === 0) return
    setViewport((prev) =>
      prev.width === nextViewport.width && prev.height === nextViewport.height
        ? prev
        : nextViewport,
    )
    applyFit(nextViewport)
  }, [renderer.pages, state.fitMode, state.rotation, applyFit])

  // Scroll-position reset on source change isn't needed here: the outer
  // `<PdfViewer key={source.id} ...>` / `<ImageViewer key={source.id} ...>`
  // in DocumentViewer remounts this component when the document changes,
  // so the scrollRef + visiblePages state are fresh by the time we render.

  // Preserve scroll position around scale changes: snapshot the relative
  // offset of the current page before scale updates, restore it after.
  const scaleAnchorRef = useRef<{
    pageIndex: number
    relativeTop: number
  } | null>(null)

  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const pageEl = pageRefs.current.get(state.currentPageIndex)
    if (!pageEl) return
    scaleAnchorRef.current = {
      pageIndex: state.currentPageIndex,
      relativeTop: pageEl.offsetTop - el.scrollTop,
    }
    // We capture on every render with the current scale; the restore in
    // the next effect runs after the DOM has reflowed for the new scale.
  }, [state.currentPageIndex])

  useLayoutEffect(() => {
    const el = scrollRef.current
    const anchor = scaleAnchorRef.current
    if (!el || !anchor) return
    const pageEl = pageRefs.current.get(anchor.pageIndex)
    if (!pageEl) return
    const target = pageEl.offsetTop - anchor.relativeTop
    if (Math.abs(target - el.scrollTop) > 1) {
      el.scrollTop = Math.max(0, target)
    }
  }, [state.scale, state.rotation])

  // IntersectionObserver picks the current page + drives virtualization.
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    const observer = new IntersectionObserver(
      (entries) => {
        let bestPage: number | null = null
        let bestRatio = 0
        const visible = new Set<number>()
        for (const entry of entries) {
          const idxAttr = entry.target.getAttribute('data-page-index')
          if (!idxAttr) continue
          const idx = Number(idxAttr)
          if (entry.isIntersecting) {
            visible.add(idx)
            if (entry.intersectionRatio > bestRatio) {
              bestRatio = entry.intersectionRatio
              bestPage = idx
            }
          }
        }
        setVisiblePages((prev) => {
          const next = new Set(prev)
          // Drop anything that's no longer reported by the observer.
          for (const i of prev) {
            const stillSeen = entries.find(
              (e) => Number(e.target.getAttribute('data-page-index')) === i,
            )
            if (stillSeen && !stillSeen.isIntersecting) next.delete(i)
          }
          for (const i of visible) next.add(i)
          // Always include a buffer either side so canvases stay mounted
          // while the user scrolls.
          for (const i of visible) {
            for (let off = 1; off <= PAGE_BUFFER; off++) {
              if (i - off >= 0) next.add(i - off)
              if (i + off < renderer.pages.length) next.add(i + off)
            }
          }
          return next
        })
        if (bestPage != null) {
          setCurrentPageIndex(bestPage)
        }
      },
      {
        root,
        threshold: [0, 0.25, 0.5, 0.75, 1],
        rootMargin: '20% 0px',
      },
    )

    for (const el of pageRefs.current.values()) observer.observe(el)
    return () => observer.disconnect()
  }, [renderer.pages, setCurrentPageIndex])

  // Track active-scroll bursts so overlay layers can opt into deferring
  // expensive work. We only flip state on rising/falling edge — not per
  // scroll event — so React doesn't re-render on every wheel tick.
  useEffect(() => {
    const root = scrollRef.current
    if (!root) return
    let timeout: ReturnType<typeof setTimeout> | null = null
    let armed = false
    const onScroll = () => {
      if (!armed) {
        armed = true
        setIsScrolling(true)
      }
      if (timeout) clearTimeout(timeout)
      timeout = setTimeout(() => {
        armed = false
        setIsScrolling(false)
      }, SCROLL_IDLE_MS)
    }
    root.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      root.removeEventListener('scroll', onScroll)
      if (timeout) clearTimeout(timeout)
    }
  }, [])

  const scrollToPage = useCallback(
    (index: number) => {
      const el = scrollRef.current
      const pageEl = pageRefs.current.get(index)
      if (!el || !pageEl) return
      setVisiblePages((prev) => {
        const next = new Set(prev)
        next.add(index)
        for (let off = 1; off <= PAGE_BUFFER; off++) {
          if (index - off >= 0) next.add(index - off)
          if (index + off < renderer.pages.length) next.add(index + off)
        }
        return next
      })
      const target = pageEl.offsetTop - 16
      el.scrollTo({ top: Math.max(0, target), behavior: 'smooth' })
    },
    [renderer.pages.length],
  )

  // External page-jump requests (e.g. a review panel pin click) fire here.
  // We dedupe on `token` so re-renders with the same token don't re-scroll,
  // but the same `pageNumber` with a bumped `token` does (user clicks the
  // same pin twice → re-scroll, in case they nudged the viewer in between).
  // Depending on `viewer.setCurrentPageIndex` rather than the whole `viewer`
  // object keeps the dep array stable across state changes (the setter is
  // memoised by useViewerState).
  const lastJumpTokenRef = useRef<number | null>(null)
  useEffect(() => {
    if (!pageJump) return
    if (lastJumpTokenRef.current === pageJump.token) return
    if (renderer.pages.length === 0) return
    const idx = Math.max(
      0,
      Math.min(pageJump.pageNumber - 1, renderer.pages.length - 1),
    )
    lastJumpTokenRef.current = pageJump.token
    setCurrentPageIndex(idx)
    scrollToPage(idx)
  }, [pageJump, renderer.pages.length, scrollToPage, setCurrentPageIndex])

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.defaultPrevented) return
    if (e.metaKey || e.ctrlKey || e.altKey) return
    switch (e.key) {
      case 'ArrowDown':
      case 'PageDown':
      case 'j': {
        if (state.currentPageIndex < renderer.pages.length - 1) {
          e.preventDefault()
          viewer.goNext()
          scrollToPage(state.currentPageIndex + 1)
        }
        break
      }
      case 'ArrowUp':
      case 'PageUp':
      case 'k': {
        if (state.currentPageIndex > 0) {
          e.preventDefault()
          viewer.goPrev()
          scrollToPage(state.currentPageIndex - 1)
        }
        break
      }
      case 'Home': {
        e.preventDefault()
        viewer.setCurrentPageIndex(0)
        scrollToPage(0)
        break
      }
      case 'End': {
        e.preventDefault()
        const last = renderer.pages.length - 1
        viewer.setCurrentPageIndex(last)
        scrollToPage(last)
        break
      }
      case '+':
      case '=': {
        e.preventDefault()
        viewer.zoomIn()
        break
      }
      case '-':
      case '_': {
        e.preventDefault()
        viewer.zoomOut()
        break
      }
      case '0': {
        e.preventDefault()
        viewer.setFitMode('page')
        break
      }
    }
  }

  useEffect(() => {
    if (autoFocus) scrollRef.current?.focus()
  }, [autoFocus])

  // Notify the parent each time the active page changes — initial mount,
  // user scroll (via the IntersectionObserver above), keyboard nav, toolbar,
  // and external page-jumps all funnel through `state.currentPageIndex`.
  useEffect(() => {
    onCurrentPageChange?.(state.currentPageIndex + 1)
  }, [state.currentPageIndex, onCurrentPageChange])

  const totalPages = renderer.pages.length

  if (renderer.loading && totalPages === 0) {
    return (
      <div
        className={cn('flex h-64 items-center justify-center', className)}
        data-testid="viewer-loading"
      >
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (renderer.error && totalPages === 0) {
    return (
      <div
        className={cn(
          'flex h-64 flex-col items-center justify-center gap-1 text-sm text-destructive',
          className,
        )}
        data-testid="viewer-error"
      >
        <span>Failed to load document.</span>
        <span className="text-xs text-muted-foreground">
          {renderer.error.message}
        </span>
      </div>
    )
  }

  if (totalPages === 0) {
    return (
      <div
        className={cn(
          'flex h-64 items-center justify-center text-sm text-muted-foreground',
          className,
        )}
        data-testid="viewer-empty"
      >
        This document has no pages.
      </div>
    )
  }

  return (
    <div
      className={cn(
        'flex h-full min-h-[24rem] flex-col overflow-hidden rounded-md border border-border bg-muted/30',
        className,
      )}
      data-testid="document-viewer"
    >
      <ViewerToolbar
        currentPage={state.currentPageIndex + 1}
        totalPages={totalPages}
        scale={state.scale}
        fitMode={state.fitMode}
        rotation={state.rotation}
        onGoToPage={(n) => {
          viewer.goToPage(n)
          scrollToPage(n - 1)
        }}
        onPrev={() => {
          viewer.goPrev()
          scrollToPage(state.currentPageIndex - 1)
        }}
        onNext={() => {
          viewer.goNext()
          scrollToPage(state.currentPageIndex + 1)
        }}
        onZoomIn={viewer.zoomIn}
        onZoomOut={viewer.zoomOut}
        onFit={viewer.setFitMode}
        onRotate={() => {
          const next = (((state.rotation + 90) % 360) as Rotation)
          viewer.setRotation(next)
        }}
      />
      <div
        ref={scrollRef}
        tabIndex={0}
        role="region"
        aria-label="Document content"
        aria-roledescription="document viewer"
        onKeyDown={onKeyDown}
        className="relative flex-1 overflow-auto outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
        data-testid="viewer-scroll"
        data-scrolling={isScrolling ? 'true' : 'false'}
      >
        <div className="flex flex-col items-center gap-4 py-4">
          {renderer.pages.map((page) => {
            const rendered = renderedPageSize(
              page.intrinsic,
              state.scale,
              state.rotation,
            )
            const isMounted = visiblePages.has(page.index)
            const slotStyle: CSSProperties = {
              width: `${Math.floor(rendered.width)}px`,
              height: `${Math.floor(rendered.height)}px`,
            }
            // Keep the same DOM/canvas slot across live-preview source
            // swaps. `page.id` includes the PDF source version, and keying
            // by it replaces the canvas with a blank one during the final
            // field-edit render cut-over.
            return (
              <div
                key={page.pageNumber}
                ref={(el) => {
                  if (el) pageRefs.current.set(page.index, el)
                  else pageRefs.current.delete(page.index)
                }}
                data-page-index={page.index}
                data-page-number={page.pageNumber}
                data-testid="viewer-page-slot"
                className="relative"
                style={slotStyle}
              >
                {isMounted ? (
                  <PageCanvas
                    page={page}
                    scale={state.scale}
                    rotation={state.rotation}
                    renderer={renderer}
                    active
                  />
                ) : (
                  <PagePlaceholder
                    page={page}
                    scale={state.scale}
                    rotation={state.rotation}
                  />
                )}
                {renderOverlay && isMounted && (
                  <div
                    className="pointer-events-none absolute inset-0"
                    data-testid="viewer-overlay-layer"
                    data-scrolling={isScrolling ? 'true' : 'false'}
                  >
                    {renderOverlay({
                      page,
                      scale: state.scale,
                      rotation: state.rotation,
                      rendered,
                      scrolling: isScrolling,
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

interface ViewerToolbarProps {
  currentPage: number
  totalPages: number
  scale: number
  fitMode: FitMode
  rotation: Rotation
  onGoToPage: (n: number) => void
  onPrev: () => void
  onNext: () => void
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: (mode: FitMode) => void
  onRotate: () => void
}

function ViewerToolbar({
  currentPage,
  totalPages,
  scale,
  fitMode,
  rotation,
  onGoToPage,
  onPrev,
  onNext,
  onZoomIn,
  onZoomOut,
  onFit,
  onRotate,
}: ViewerToolbarProps) {
  // Mirror the externally-controlled currentPage into the local input
  // draft using the "previous-prop" pattern — adjusting state during
  // render is the React-recommended replacement for a sync effect.
  const [draftPage, setDraftPage] = useState(String(currentPage))
  const [prevCurrent, setPrevCurrent] = useState(currentPage)
  if (prevCurrent !== currentPage) {
    setPrevCurrent(currentPage)
    setDraftPage(String(currentPage))
  }

  const submitPage = () => {
    const n = Number.parseInt(draftPage, 10)
    if (Number.isFinite(n) && n >= 1 && n <= totalPages) {
      onGoToPage(n)
    } else {
      setDraftPage(String(currentPage))
    }
  }

  return (
    <div
      className="flex min-h-10 flex-nowrap items-center gap-1 overflow-x-auto overscroll-x-contain border-b border-border bg-background/80 px-1.5 py-1 backdrop-blur md:min-h-12 md:gap-2 md:px-3 md:py-2"
      data-testid="viewer-toolbar"
    >
      <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted/50 p-0.5 md:gap-1 md:p-1">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={onPrev}
          disabled={currentPage <= 1}
          aria-label="Previous page"
          data-testid="viewer-prev-page"
          className="size-8 md:size-9"
        >
          <ChevronLeft />
        </Button>
        <div className="flex items-center gap-1 px-1 text-xs text-muted-foreground md:px-2">
          <input
            type="text"
            inputMode="numeric"
            value={draftPage}
            aria-label="Current page"
            data-testid="viewer-page-input"
            className="h-7 w-9 rounded-sm border border-input bg-card px-1 text-center text-xs outline-none focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring/30 md:h-8 md:w-10"
            onChange={(e) => setDraftPage(e.target.value)}
            onBlur={submitPage}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                submitPage()
              }
            }}
          />
          <span className="whitespace-nowrap">/ {totalPages}</span>
        </div>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={onNext}
          disabled={currentPage >= totalPages}
          aria-label="Next page"
          data-testid="viewer-next-page"
          className="size-8 md:size-9"
        >
          <ChevronRight />
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted/50 p-0.5 md:gap-1 md:p-1">
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={onZoomOut}
          aria-label="Zoom out"
          data-testid="viewer-zoom-out"
          className="size-8 md:size-9"
        >
          <Minus />
        </Button>
        <span
          className="min-w-11 px-1 text-center text-xs text-muted-foreground md:min-w-12"
          data-testid="viewer-zoom-label"
        >
          {Math.round(scale * 100)}%
        </span>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={onZoomIn}
          aria-label="Zoom in"
          data-testid="viewer-zoom-in"
          className="size-8 md:size-9"
        >
          <Plus />
        </Button>
      </div>

      <div className="flex shrink-0 items-center gap-0.5 rounded-md bg-muted/50 p-0.5 md:gap-1 md:p-1">
        <Button
          type="button"
          size="icon-sm"
          variant={fitMode === 'width' ? 'soft' : 'ghost'}
          onClick={() => onFit('width')}
          aria-label="Fit to width"
          aria-pressed={fitMode === 'width'}
          data-testid="viewer-fit-width"
          className="size-8 md:size-9"
        >
          <RectangleHorizontal />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant={fitMode === 'page' ? 'soft' : 'ghost'}
          onClick={() => onFit('page')}
          aria-label="Fit to page"
          aria-pressed={fitMode === 'page'}
          data-testid="viewer-fit-page"
          className="size-8 md:size-9"
        >
          <Maximize2 />
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          onClick={onRotate}
          aria-label={`Rotate (current ${rotation}°)`}
          data-testid="viewer-rotate"
          className="size-8 md:size-9"
        >
          <RotateCw />
        </Button>
      </div>
    </div>
  )
}
